import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Divider,
  Typography,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  PAGE_TYPE_MASTER,
  PRIORITY_MASTER,
} from "../../../../constants/suggestOptions";
import { FONT_SIZE } from "../../../../constants/fontSizes";

/**
 * 企業向けサジェストウィジェット本文
 */
export default function ProjectHelpBodyCompany({ formState }) {
  // 行ごとの一意なIDを作るためのカウンター（サーバー未保存の行に使う）
  const idRef = useRef(1);
  const generateTempId = () => `temp-${idRef.current++}`;

  // 親から渡された suggestions（無ければ空配列）
  const suggestions = Array.isArray(formState?.suggestions)
    ? formState.suggestions
    : [];

  // 親から渡された操作関数
  const onSave = formState?.onSave;
  const onRequestDelete = formState?.onRequestDelete;
  const isDeleting = formState?.isDeleting ?? false;

  // 選択中の画面（PAGE_TYPE_MASTER.value）
  const pageValue = formState?.pageType ?? PAGE_TYPE_MASTER[0]?.value ?? "";
  const setPageValue = formState?.setPageType;

  // 保存中
  const [isSaving, setIsSaving] = useState(false);
  const [rows, setRows] = useState([]);

  /**
   * 編集行のDOM参照（クリックアウト判定用）
   * - row.client_id をキーにして該当行の要素を保持する
   */
  const rowRefs = useRef({}); // { [client_id]: HTMLElement }

  const normalize = useCallback((v) => (v ?? "").trim(), []);
  const signature = useCallback(
    (r) => `${r.page_type}||${r.priority ?? "medium"}||${normalize(r.content)}`,
    [normalize],
  );

  /**
   * サーバー suggestions が変わったら rows を同期する
   * - isNew: ローカルで追加した未保存行（先頭に残す）
   * - isPending: 保存後にサーバーから再取得されるまでの一時状態（同一内容がサーバーに来たら消える）
   * - isEditing/draft: 編集状態は可能な限り引き継ぐ
   */
  useEffect(() => {
    setRows((prevRows) => {
      const prevById = new Map(
        prevRows.filter((r) => r.id != null).map((r) => [r.id, r]),
      );

      // サーバー行を rows 用に整形しつつ、前回の編集状態を引き継ぐ
      const serverRows = suggestions.map((s) => {
        const prev = s.id != null ? prevById.get(s.id) : undefined;

        const baseContent = s.content ?? "";
        const basePriority = s.priority ?? "medium";

        return {
          client_id: prev?.client_id ?? s.id ?? generateTempId(),
          ...s,
          isNew: false,
          isPending: false,
          isEditing: prev?.isEditing ?? false,
          // 編集中なら draft を維持。そうでなければサーバー値に合わせる
          draft: prev?.isEditing
            ? prev.draft
            : { content: baseContent, priority: basePriority },
        };
      });

      // サーバーに既に存在する行（同一シグネチャ）の pending を消すためのSet
      const serverSigSet = new Set(serverRows.map(signature));

      // ローカル新規行は常に残す（先頭に表示したいので後で前に足す）
      const localNewRows = prevRows.filter((r) => r.isNew);

      // pending のうち、サーバーに同じ内容が来ていないものだけ残す
      const pendingToKeep = prevRows
        .filter((r) => r.isPending)
        .filter((r) => !serverSigSet.has(signature(r)));

      // 表示順：新規（先頭）→ pending（必要なものだけ）→ サーバー行
      return [...localNewRows, ...pendingToKeep, ...serverRows];
    });
  }, [suggestions, signature]);

  // 共通スタイル
  const createFieldSx = (fontSize) => ({
    "& .MuiOutlinedInput-root": {
      height: 36,
      alignItems: "center",
      backgroundColor: "#fff",

      "& fieldset": {
        border: "1px solid rgba(0, 0, 0, 0.23)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(0, 0, 0, 0.87)",
      },
      "&.Mui-focused fieldset": {
        borderWidth: "2px",
        borderColor: "primary.main",
      },
      // フォーカスが当たった時に背景色が青くなるのを防ぐ
      "&.Mui-focused": {
        backgroundColor: "#fff",
      },
    },
    "& .MuiOutlinedInput-input": {
      fontSize,
      py: 0,
      outline: "none",
      boxShadow: "none",
      "&:focus": { boxShadow: "none" },
      "&:-webkit-autofill": {
        WebkitBoxShadow: "0 0 0 100px #fff inset !important",
        WebkitTextFillColor: "inherit !important",
        transition: "background-color 5000s ease-in-out 0s",
      },
    },
    "& .MuiSelect-select": {
      fontSize,
      py: 0,
      display: "flex",
      alignItems: "center",
      backgroundColor: "transparent !important",
      "&:focus": { backgroundColor: "transparent" },
    },
  });

  const fieldSxPage = createFieldSx(FONT_SIZE.SM);
  const fieldSxRow = createFieldSx(FONT_SIZE.XS);

  // セレクトのメニュー表示調整
  const menuProps = useMemo(
    () => ({
      disablePortal: true,
      PaperProps: { sx: { zIndex: 2000, maxHeight: 200 } },
    }),
    [],
  );

  // PRIORITY_MASTER から MenuItem を生成（定義を一元化）
  const priorityOptions = useMemo(
    () =>
      PRIORITY_MASTER.map((p) => (
        <MenuItem key={p.value} value={p.value} sx={{ fontSize: FONT_SIZE.XS }}>
          {p.label}
        </MenuItem>
      )),
    [],
  );

  // 今選択中の画面だけ抽出（並びは rows の順を維持）
  const currentRows = useMemo(
    () => rows.filter((r) => r.page_type === pageValue),
    [rows, pageValue],
  );

  // 指定した行だけ部分更新（新規行の直値更新用）
  const updateRow = useCallback((clientId, key, value) => {
    setRows((prev) =>
      prev.map((r) => (r.client_id === clientId ? { ...r, [key]: value } : r)),
    );
  }, []);

  // draft 更新（編集行の下書き更新用）
  const updateDraft = useCallback((clientId, key, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.client_id === clientId
          ? { ...r, draft: { ...(r.draft ?? {}), [key]: value } }
          : r,
      ),
    );
  }, []);

  // 新規行を先頭に追加
  const addRow = useCallback(() => {
    setRows((prev) => [
      {
        client_id: generateTempId(),
        page_type: pageValue,
        content: "",
        priority: "medium",
        isNew: true,
        isPending: false,
        isEditing: false,
        draft: { content: "", priority: "medium" },
      },
      ...prev,
    ]);
  }, [pageValue]);

  // ダブルクリックで編集開始（pending中は編集禁止）
  const startEdit = useCallback(
    (clientId) => {
      if (isSaving || isDeleting) return;

      setRows((prev) =>
        prev.map((r) => {
          if (r.client_id !== clientId) return r;
          if (r.isPending) return r;

          return {
            ...r,
            isEditing: true,
            draft: {
              content: r.content ?? "",
              priority: r.priority ?? "medium",
            },
          };
        }),
      );
    },
    [isSaving, isDeleting],
  );

  // 編集行の「変更あり」判定（空は不可）
  const isEditingRowModified = useCallback((row) => {
    if (!row.isEditing) return false;
    if (row.isPending) return false;

    const originalContent = (row.content ?? "").trim();
    const originalPriority = row.priority ?? "medium";

    const draftContent = (row.draft?.content ?? "").trim();
    const draftPriority = row.draft?.priority ?? originalPriority;

    if (draftContent === "") return false;
    return (
      draftContent !== originalContent || draftPriority !== originalPriority
    );
  }, []);

  /**
   * クリックアウト：
   * - 編集行の外側をクリックしたとき、
   *   - 変更あり：編集継続
   *   - 変更なし：編集を閉じて draft を元に戻す
   */
  useEffect(() => {
    const handleMouseDown = (event) => {
      if (isSaving || isDeleting) return;

      setRows((prev) => {
        let changed = false;

        const next = prev.map((row) => {
          if (!row.isEditing) return row;

          const el = rowRefs.current[row.client_id];
          const clickedInside = el ? el.contains(event.target) : false;

          if (clickedInside) return row;

          // 外側クリックでも変更があるなら編集継続
          if (isEditingRowModified(row)) return row;

          // 変更なしなら閉じる
          changed = true;
          return {
            ...row,
            isEditing: false,
            draft: {
              content: row.content ?? "",
              priority: row.priority ?? "medium",
            },
          };
        });

        return changed ? next : prev;
      });
    };

    document.addEventListener("mousedown", handleMouseDown, true);
    return () =>
      document.removeEventListener("mousedown", handleMouseDown, true);
  }, [isSaving, isDeleting, isEditingRowModified]);

  // 保存対象かどうか（新規 or 編集どちらにも対応）
  const isRowModified = useCallback((row) => {
    if (row.isPending) return false;

    // 新規行は row 直下を見て空でなければ対象
    if (row.isNew) return (row.content ?? "").trim() !== "";

    // 既存行は draft を見て判定（空は不可）
    const originalContent = (row.content ?? "").trim();
    const originalPriority = row.priority ?? "medium";

    const draftContent = (row.draft?.content ?? "").trim();
    const draftPriority = row.draft?.priority ?? originalPriority;

    if (draftContent === "") return false;
    return (
      draftContent !== originalContent || draftPriority !== originalPriority
    );
  }, []);

  /**
   * 保存：
   * - currentRows のうち「変更あり」だけを payload 化して onSave に渡す
   * - 成功後は isPending にして、サーバー再取得(useEffect)を待つ
   */
  const saveAll = useCallback(async () => {
    if (isSaving) return;

    const targetRows = currentRows.filter((row) => isRowModified(row));
    if (targetRows.length === 0) return;

    const sentClientIds = new Set(targetRows.map((r) => r.client_id));

    const payload = targetRows.map((row) => {
      const isActuallyNew = row.isNew && !row.id;

      const contentValue = isActuallyNew
        ? (row.content ?? "").trim()
        : (row.draft?.content ?? row.content ?? "").trim();

      const priorityValue = isActuallyNew
        ? (row.priority ?? "medium")
        : (row.draft?.priority ?? row.priority ?? "medium");

      const base = {
        page_type: row.page_type,
        content: contentValue,
        priority: priorityValue,
      };

      // 既存行（編集）は id を付けて更新扱いにする
      return isActuallyNew ? base : { ...base, id: row.id };
    });

    setIsSaving(true);

    try {
      await onSave?.(payload);

      setRows((prev) =>
        prev.map((r) => {
          if (!sentClientIds.has(r.client_id)) return r;

          const finalContent = (
            r.isNew ? (r.content ?? "") : (r.draft?.content ?? r.content ?? "")
          ).trim();
          const finalPriority = r.isNew
            ? (r.priority ?? "medium")
            : (r.draft?.priority ?? r.priority ?? "medium");

          return {
            ...r,
            content: finalContent,
            priority: finalPriority,
            isNew: false,
            isEditing: false,
            isPending: true, // サーバー反映待ち（同一内容が来たら同期で置き換わる/消える）
            draft: { content: finalContent, priority: finalPriority },
          };
        }),
      );
    } catch (e) {
      console.error("保存失敗:", e);
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, currentRows, isRowModified, onSave]);

  const canSave = useMemo(
    () => currentRows.some((r) => isRowModified(r)),
    [currentRows, isRowModified],
  );

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      {/* 画面タイプ選択 */}
      <Box sx={{ mb: 1.25 }}>
        <TextField
          select
          fullWidth
          size="small"
          value={pageValue}
          onChange={(e) => setPageValue?.(e.target.value)}
          sx={fieldSxPage}
          SelectProps={{ MenuProps: menuProps }}
        >
          {PAGE_TYPE_MASTER.map((p) => (
            <MenuItem
              key={p.value}
              value={p.value}
              sx={{ fontSize: FONT_SIZE.XS }}
            >
              {p.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Divider sx={{ my: 1.25 }} />

      {/* 一覧（スクロール） */}
      <Box sx={{ maxHeight: 250, overflowY: "auto" }}>
        {currentRows.map((row) => (
          <Box
            key={row.client_id}
            sx={{ mb: 1 }}
            ref={(el) => {
              if (!el) return;
              rowRefs.current[row.client_id] = el;
            }}
          >
            {row.isNew ? (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  fullWidth
                  placeholder="内容を入力"
                  value={row.content ?? ""}
                  onChange={(e) =>
                    updateRow(row.client_id, "content", e.target.value)
                  }
                  sx={fieldSxRow}
                  disabled={isSaving || isDeleting || row.isPending}
                />
                <TextField
                  select
                  value={row.priority ?? "medium"}
                  onChange={(e) =>
                    updateRow(row.client_id, "priority", e.target.value)
                  }
                  sx={{ ...fieldSxRow, width: 80 }}
                  SelectProps={{ MenuProps: menuProps }}
                  disabled={isSaving || isDeleting || row.isPending}
                >
                  {priorityOptions}
                </TextField>
                <IconButton
                  size="small"
                  onClick={() => {
                    // 未入力の新規行はローカルから消す（サーバーには存在しないため）
                    if ((row.content ?? "").trim() === "") {
                      setRows((prev) =>
                        prev.filter((r) => r.client_id !== row.client_id),
                      );
                    } else {
                      onRequestDelete?.(row);
                    }
                  }}
                  disabled={isSaving || isDeleting || row.isPending}
                >
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </Box>
            ) : row.isEditing ? (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  fullWidth
                  placeholder="内容を入力"
                  value={row.draft?.content ?? ""}
                  onChange={(e) =>
                    updateDraft(row.client_id, "content", e.target.value)
                  }
                  sx={fieldSxRow}
                  disabled={isSaving || isDeleting || row.isPending}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      // Escapeで編集終了（draftを元に戻す）
                      setRows((prev) =>
                        prev.map((r) =>
                          r.client_id === row.client_id
                            ? {
                                ...r,
                                isEditing: false,
                                draft: {
                                  content: r.content ?? "",
                                  priority: r.priority ?? "medium",
                                },
                              }
                            : r,
                        ),
                      );
                    }
                  }}
                />
                <TextField
                  select
                  value={row.draft?.priority ?? "medium"}
                  onChange={(e) =>
                    updateDraft(row.client_id, "priority", e.target.value)
                  }
                  sx={{ ...fieldSxRow, width: 80 }}
                  SelectProps={{ MenuProps: menuProps }}
                  disabled={isSaving || isDeleting || row.isPending}
                >
                  {priorityOptions}
                </TextField>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRequestDelete?.(row);
                  }}
                  disabled={isSaving || isDeleting || row.isPending}
                >
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </Box>
            ) : (
              <Box
                onDoubleClick={() => startEdit(row.client_id)}
                sx={{
                  display: "flex",
                  border: "1px solid",
                  borderColor: "grey.200",
                  borderRadius: 1,
                  bgcolor: "grey.50",
                  minHeight: 36,
                  cursor: row.isPending ? "not-allowed" : "pointer",
                  opacity: row.isPending ? 0.6 : 1,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    px: 1.5,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: FONT_SIZE.XS }}>
                    {row.content}
                  </Typography>
                </Box>

                <Box sx={{ width: "1px", bgcolor: "grey.300", my: 0.8 }} />

                <Box
                  sx={{
                    width: 60,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: 10, fontWeight: "bold" }}>
                    {PRIORITY_MASTER.find((p) => p.value === row.priority)
                      ?.label ?? ""}
                  </Typography>
                </Box>

                <Box sx={{ width: "1px", bgcolor: "grey.300", my: 0.8 }} />

                <Box
                  sx={{
                    width: 44,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRequestDelete?.(row);
                    }}
                    onDoubleClick={(e) => e.stopPropagation()}
                    disabled={isSaving || isDeleting || row.isPending}
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 1.25 }} />

      {/* 操作ボタン */}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          variant="outlined"
          fullWidth
          onClick={addRow}
          startIcon={<AddIcon />}
          disabled={isSaving || isDeleting}
        >
          追加
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={saveAll}
          disabled={!canSave || isSaving || isDeleting}
        >
          {isSaving ? "保存中…" : "保存"}
        </Button>
      </Box>
    </Box>
  );
}
